/**
 * FlightPlanPanel — Create, edit, and manage VFR/IFR flight plans.
 */
import { useState, type FormEvent } from 'react';
import { useFlightPlan } from '../services/FlightPlanContext';

export function FlightPlanPanel() {
  const {
    currentPlan, planList, loading, error,
    createPlan, loadPlan, deletePlan, generateNavLog, clearCurrentPlan,
  } = useFlightPlan();

  const [departure, setDeparture] = useState('');
  const [destination, setDestination] = useState('');
  const [intermediates, setIntermediates] = useState('');
  const [flightRules, setFlightRules] = useState<'VFR' | 'IFR'>('VFR');
  const [cruiseAltitude, setCruiseAltitude] = useState(5500);
  const [trueAirspeed, setTrueAirspeed] = useState(120);
  const [showList, setShowList] = useState(false);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!departure || !destination) return;
    await createPlan({
      departure, destination,
      intermediates: intermediates ? intermediates.split(/[\s,]+/).filter(Boolean) : undefined,
      flightRules, cruiseAltitude, trueAirspeed,
    });
  };

  return (
    <div className="panel flight-plan-panel" role="region" aria-label="Flight Plan">
      <h2>Flight Plan</h2>

      {error && <div className="panel-error" role="alert">{error}</div>}

      {!currentPlan && !showList && (
        <form onSubmit={handleCreate} className="fp-form">
          <div className="fp-field">
            <label htmlFor="fp-dep">Departure</label>
            <input id="fp-dep" value={departure} onChange={e => setDeparture(e.target.value.toUpperCase())}
              placeholder="KJFK" maxLength={4} required aria-required="true" />
          </div>
          <div className="fp-field">
            <label htmlFor="fp-dest">Destination</label>
            <input id="fp-dest" value={destination} onChange={e => setDestination(e.target.value.toUpperCase())}
              placeholder="KBOS" maxLength={4} required aria-required="true" />
          </div>
          <div className="fp-field">
            <label htmlFor="fp-int">Intermediates</label>
            <input id="fp-int" value={intermediates} onChange={e => setIntermediates(e.target.value.toUpperCase())}
              placeholder="KLGA KEWR" />
          </div>
          <div className="fp-row">
            <div className="fp-field">
              <label htmlFor="fp-rules">Rules</label>
              <select id="fp-rules" value={flightRules} onChange={e => setFlightRules(e.target.value as 'VFR' | 'IFR')}>
                <option value="VFR">VFR</option>
                <option value="IFR">IFR</option>
              </select>
            </div>
            <div className="fp-field">
              <label htmlFor="fp-alt">Altitude (ft)</label>
              <input id="fp-alt" type="number" value={cruiseAltitude}
                onChange={e => setCruiseAltitude(Number(e.target.value))} min={500} max={45000} step={500} />
            </div>
            <div className="fp-field">
              <label htmlFor="fp-tas">TAS (kt)</label>
              <input id="fp-tas" type="number" value={trueAirspeed}
                onChange={e => setTrueAirspeed(Number(e.target.value))} min={40} max={500} />
            </div>
          </div>
          <button type="submit" disabled={loading} className="fp-btn fp-btn-primary">
            {loading ? 'Computing…' : 'Create Plan'}
          </button>
          <button type="button" className="fp-btn" onClick={() => setShowList(true)}>
            Saved Plans ({planList.length})
          </button>
        </form>
      )}

      {currentPlan && (
        <div className="fp-summary">
          <h3>{currentPlan.name}</h3>
          <div className="fp-meta">
            <span>{currentPlan.departure.identifier} → {currentPlan.destination.identifier}</span>
            <span>{currentPlan.flightRules} | {currentPlan.cruiseAltitude}ft</span>
            <span>{currentPlan.totalDistanceNm.toFixed(1)} nm | {Math.round(currentPlan.totalTimeMinutes)} min</span>
          </div>
          <div className="fp-waypoints">
            {currentPlan.waypoints.map(wp => (
              <span key={wp.sequenceIndex} className="fp-wp">{wp.identifier}</span>
            ))}
          </div>
          {currentPlan.navLog && (
            <div className="fp-fuel">
              Fuel: {currentPlan.navLog.totalFuelGallons.toFixed(1)} gal
            </div>
          )}
          <div className="fp-actions">
            <button className="fp-btn" onClick={() => generateNavLog(currentPlan.id)}
              disabled={loading}>
              {loading ? 'Generating…' : 'Generate NavLog'}
            </button>
            <button className="fp-btn fp-btn-danger" onClick={() => deletePlan(currentPlan.id)}>Delete</button>
            <button className="fp-btn" onClick={() => { clearCurrentPlan(); setShowList(false); }}>New Plan</button>
          </div>
        </div>
      )}

      {showList && !currentPlan && (
        <div className="fp-list">
          <button className="fp-btn" onClick={() => setShowList(false)}>← Back</button>
          {planList.length === 0 && <p>No saved plans.</p>}
          {planList.map(p => (
            <div key={p.id} className="fp-list-item" onClick={() => { loadPlan(p.id); setShowList(false); }}
              role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && loadPlan(p.id)}>
              <strong>{p.name}</strong>
              <span>{p.departure} → {p.destination} | {p.totalDistanceNm.toFixed(0)} nm</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
